/**
 * Navigation Hook
 * Centralized navigation structure for the Orion SaaS application.
 * 
 * Organized into 5 logical business domains:
 * - Sales (Commercial Focus)
 * - Production (Operational Focus)
 * - Finance (Administrative Focus)
 * - Master DNA (Core Knowledge Base)
 * - Settings (Configuration)
 */

import { useMemo } from 'react'
import { 
  Home, 
  Users, 
  FileText, 
  ShoppingCart,
  FolderKanban,
  CalendarDays,
  Package,
  ClipboardList,
  Receipt,
  TrendingUp,
  DollarSign,
  Database,
  Wrench,
  Settings,
  Shield,
  FileSearch,
  BarChart,
  Warehouse,
  Zap,
  MapPin,
  Factory,
  Box,
  CreditCard,
  Upload,
  UploadCloud,
  Calculator,
  Building2,
  UserCog,
  Truck,
  Percent,
  Clock,
  BarChart3,
  Cpu,
  Hammer,
  DoorOpen,
  Layers,
  Palette,
  Activity,
  Dna,
  Refrigerator,
  Leaf,
  Brain,
  LifeBuoy,
  Inbox,
  Bot,
  KeyRound
} from 'lucide-react'

/**
 * Navigation structure organized by business domain
 * Memoized to ensure re-renders when language changes
 */
// Bouwt de nav-groepen als PLAIN functie (geen hook), zodat zowel useNavigation (via useMemo) als de
// vensterregistratie (getWindowRegistry) exact DEZELFDE ENE bron gebruiken. Inhoud/labels/volgorde
// ongewijzigd — dit is puur een extractie zodat de SSOT één plek heeft.
export const buildNavGroups = (translate) => {
    return [
    {
      id: 'home',
      label: translate('layout.navigation.overview', { defaultValue: 'Overzicht' }),
      icon: Home,
      collapsible: true,
      items: [
        { 
          name: translate('layout.navigation.dashboard'), 
          href: '/dashboard', 
          icon: Home, 
          resource: 'dashboard', 
          action: 'read' 
        },
      ]
    },
    {
      id: 'sales',
      label: translate('layout.navigation.sales', { defaultValue: 'Sales' }),
      icon: BarChart,
      collapsible: true,
      items: [
        { 
          name: translate('layout.navigation.klanten'), 
          href: '/klanten', 
          icon: Users, 
          resource: 'klanten', 
          action: 'read' 
        },
        {
          name: translate('layout.navigation.offertes'),
          href: '/offertes',
          icon: FileText,
          resource: 'offertes',
          action: 'read'
        },
        {
          name: translate('layout.navigation.snelofferte', { defaultValue: 'Snelofferte' }),
          href: '/snelofferte/nieuw',
          icon: Zap,
          resource: 'offertes',
          action: 'write',
        },
        {
          name: translate('agents.menu', { defaultValue: 'Digitale medewerkers' }),
          href: '/digitale-medewerkers',
          icon: Bot,
          resource: 'agents',
          action: 'read',
        },
        {
          name: translate('aiInbox.menu', { defaultValue: 'AI-inbox' }),
          href: '/ai-inbox',
          icon: Inbox,
          resource: 'ai_voorstellen',
          action: 'read',
        },
        { 
          name: translate('layout.navigation.mySales', { defaultValue: 'Mijn Verkoop' }), 
          href: '/sales/rep-portal', 
          icon: Users, 
          resource: 'sales', 
          action: 'read',
          requiresRole: ['SALES_REP']
        },
        { 
          name: translate('layout.navigation.sketchupImport', { defaultValue: 'SketchUp Import' }), 
          href: '/import/sketchup', 
          icon: Upload, 
          resource: 'projecten', 
          action: 'read'  // Changed from 'write' to 'read' - SketchUp import is part of sales workflow
        },
        { 
          name: translate('layout.navigation.orderImport', { defaultValue: 'Order Import' }), 
          href: '/orders/import', 
          icon: UploadCloud, 
          resource: 'projecten', 
          action: 'read'  // PDF order/quote import
        },
      ]
    },
    {
      id: 'production',
      label: translate('layout.navigation.productie', { defaultValue: 'Production' }),
      icon: Factory,
      collapsible: true,
      items: [
        {
          name: translate('dossiers.title', { defaultValue: 'Dossiers' }),
          href: '/dossiers',
          icon: FileText,
          resource: 'projecten',
          action: 'read',
        },
        { 
          name: translate('layout.navigation.planning'), 
          href: '/planning', 
          icon: CalendarDays, 
          resource: 'planning', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.montagePlanbord', { defaultValue: 'Montage Planbord' }), 
          href: '/planning/montage', 
          icon: CalendarDays, 
          resource: 'planning', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.productionPlanningGantt', { defaultValue: 'Productieplanning (Gantt)' }), 
          href: '/planning/gantt', 
          icon: CalendarDays, 
          resource: 'planning', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.procurementPlanning', { defaultValue: 'Inkoop & Planning' }), 
          href: '/procurement', 
          icon: ShoppingCart, 
          resource: 'materiaal', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.purchaseOrderManagement', { defaultValue: 'Bestelbonnen Beheer' }), 
          href: '/procurement/purchase-orders', 
          icon: ShoppingCart, 
          resource: 'materiaal', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.purchasingTracking', { defaultValue: 'Werkblad Bestellingen' }), 
          href: '/procurement/purchasing', 
          icon: Package, 
          resource: 'materiaal', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.leanInventoryMRP', { defaultValue: 'Lean Inventory & MRP' }), 
          href: '/inventory', 
          icon: Package, 
          resource: 'materiaal', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.werkbonnen'), 
          href: '/werkbonnen', 
          icon: ClipboardList, 
          resource: 'werkbonnen', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.productie'), 
          href: '/productie', 
          icon: Factory, 
          resource: 'productie', 
          action: 'read' 
        },
        {
          // Zaagplan v1 Z1b (#1021): zelfde recht als de backend (productie.read op GET /production/batches).
          name: translate('layout.navigation.zaagplannen', { defaultValue: 'Zaagplannen' }),
          href: '/productie/nesting-stats',
          icon: Layers,
          resource: 'productie',
          action: 'read'
        },
        { 
          name: translate('layout.navigation.bestellingen'), 
          href: '/bestellingen', 
          icon: ShoppingCart, 
          resource: 'bestellingen', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.orderStatus'), 
          href: '/order-status', 
          icon: MapPin, 
          resource: 'projecten', 
          action: 'read' 
        },
        {
          name: translate('layout.navigation.ladderlift', { defaultValue: 'Ladderlift' }),
          href: '/ladderlift',
          icon: Truck,
          resource: 'projecten',
          action: 'read'
        },
        {
          name: translate('layout.navigation.orionGreenIndex', { defaultValue: 'Orion Green Index (Eco)' }),
          href: '/sustainability',
          icon: Leaf,
          resource: 'projecten',
          action: 'read'
        },
      ]
    },
    {
      id: 'finance',
      label: translate('layout.navigation.finance', { defaultValue: 'Financieel' }),
      icon: DollarSign,
      collapsible: true,
      items: [
        { 
          name: translate('layout.navigation.financeControlCenter', { defaultValue: 'Finance Control Center' }), 
          href: '/finance/control-center', 
          icon: Calculator, 
          resource: 'financieel', 
          action: 'read',
          requiresManagement: true
        },
        { 
          name: translate('layout.navigation.businessHealthPulse', { defaultValue: 'Bedrijfsgezondheid' }), 
          href: '/finance/pulse', 
          icon: Activity, 
          resource: 'financieel', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.financeMaster'), 
          href: '/finance', 
          icon: DollarSign, 
          resource: 'financieel', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.cashflow'), 
          href: '/cashflow', 
          icon: TrendingUp, 
          resource: 'financieel', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.facturen'), 
          href: '/facturen', 
          icon: Receipt, 
          resource: 'facturen', 
          action: 'read' 
        },
        { 
          name: translate('receipts.inboxTitle', { defaultValue: 'Bonnen Inbox' }), 
          href: '/receipts/inbox', 
          icon: Receipt, 
          resource: 'projecten', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.betalingsplanning'), 
          href: '/betalingsplanning', 
          icon: CalendarDays, 
          resource: 'financieel', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.managementDashboard', { defaultValue: 'Management Dashboard' }), 
          href: '/management',
          icon: TrendingUp, 
          resource: 'finance', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.yearOverview', { defaultValue: 'Jaaroverzicht' }), 
          href: '/management/reports',
          icon: BarChart3, 
          resource: 'finance', 
          action: 'read' 
        },
        {
          name: translate('layout.navigation.installationScheduling', { defaultValue: 'Plaatsingsplanning' }),
          href: '/installation-scheduling',
          icon: CalendarDays,
          resource: 'planning',
          action: 'read'
        },
        {
          name: translate('layout.navigation.verlofBeheer', { defaultValue: 'Verlof & Beschikbaarheid' }),
          href: '/verlofbeheer',
          icon: Users,
          resource: 'planning',
          action: 'read'
        },
        { 
          name: translate('layout.navigation.billing', { defaultValue: 'Billing' }), 
          href: '/billing', 
          icon: CreditCard, 
          resource: 'billing', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.sales', { defaultValue: 'Verkoop' }), 
          href: '/sales', 
          icon: BarChart, 
          resource: 'sales', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.commercialAnalysis', { defaultValue: 'Commerciële Analyse' }), 
          href: '/analytics/sales', 
          icon: BarChart3, 
          resource: 'sales', 
          action: 'read',
          requiresRole: ['MANAGER', 'ADMIN']
        },
        { 
          name: translate('layout.navigation.commissions', { defaultValue: 'Commissies' }), 
          href: '/commissions', 
          icon: DollarSign, 
          resource: 'sales', 
          action: 'read',
          requiresRole: ['MANAGER', 'ADMIN']
        },
        { 
          name: translate('layout.navigation.analytics', { defaultValue: 'Analyses' }), 
          href: '/analytics/production-recap', 
          icon: BarChart3, 
          resource: 'finance', 
          action: 'read',
          requiresRole: ['ADMIN', 'MANAGER']
        },
      ]
    },
    {
      id: 'master-dna',
      label: translate('layout.navigation.masterDNA', { defaultValue: 'Master DNA' }),
      icon: Database,
      collapsible: true,
      items: [
        // Link naar Materialen (tabel met alle actieve materialen, direct zichtbaar)
        { 
          name: translate('layout.navigation.materialen', { defaultValue: 'Materialen' }), 
          href: '/materialen', 
          icon: Package, 
          resource: 'materiaal', 
          action: 'read' 
        },
        {
          name: translate('layout.navigation.leveranciersprijslijsten', { defaultValue: 'Leveranciersprijslijsten' }),
          href: '/materialen/leveranciersprijslijsten',
          icon: ClipboardList,
          resource: 'materiaal',
          action: 'read'
        },
        {
          name: translate('layout.navigation.materialenLibrary', { defaultValue: 'Materialen Bibliotheek' }),
          href: '/materialen/library',
          icon: Layers,
          resource: 'materiaal',
          action: 'read'
        },
        {
          name: translate('layout.navigation.kleurenbank', { defaultValue: 'Kleurenbank' }),
          href: '/materialen/kleurenbank',
          icon: Palette,
          resource: 'materiaal',
          action: 'read'
        },
        {
          name: translate('layout.navigation.apparatuur', { defaultValue: 'Apparatuur' }),
          href: '/apparatuur',
          icon: Refrigerator,
          resource: 'materiaal',
          action: 'read'
        },
        {
          name: translate('layout.navigation.storageLocations', { defaultValue: 'Magazijnlocaties' }),
          href: '/storage-locations', 
          icon: MapPin, 
          resource: 'materiaal', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.voorraad'), 
          href: '/voorraad', 
          icon: Warehouse, 
          resource: 'materiaal', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.masterDNA'), 
          href: '/master-dna', 
          icon: Database, 
          resource: 'instellingen', 
          action: 'read' 
        },
        {
          name: translate('layout.navigation.kasten'),
          href: '/kasten',
          icon: Box,
          resource: 'projecten',
          action: 'write'
        },
        // C8 (BLOK C / Sprint 1): Kast Wizard sidebar-link verwijderd —
        // kast-configuratie loopt via /kasten (KastConfiguratorPage).
        {
          name: translate('layout.navigation.bibliotheek', { defaultValue: 'Productbibliotheek' }), 
          href: '/bibliotheek', 
          icon: Layers, 
          resource: 'projecten', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.pivotDoorWizard', { defaultValue: 'Pivot Deur Wizard' }), 
          href: '/pivot-deur-wizard', 
          icon: DoorOpen, 
          resource: 'projecten', 
          action: 'write' 
        },
        { 
          name: translate('layout.navigation.ruimtes'), 
          href: '/ruimtes', 
          icon: Home, 
          resource: 'projecten', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.leveranciers'), 
          href: '/leveranciers', 
          icon: Truck, 
          resource: 'leveranciers', 
          action: 'read' 
        },
        {
          name: translate('layout.navigation.merkenKortingen', { defaultValue: 'Merken & kortingen' }),
          href: '/merken-kortingen',
          icon: Percent,
          resource: 'leveranciers',
          action: 'read'
        },
      ]
    },
    {
      id: 'professional',
      label: translate('layout.navigation.professional.label', { defaultValue: 'Professionals' }),
      icon: Building2,
      collapsible: true,
      items: [
        {
          name: translate('layout.navigation.projecten'),
          href: '/projecten',
          icon: FolderKanban,
          resource: 'projecten',
          action: 'read',
          isExpandable: true, // Alle Dossiers, Projecten Overzicht, Projecten Dashboard, Nieuw Dossier, Recent
        },
      ]
    },
    {
      id: 'settings',
      label: translate('layout.navigation.settings', { defaultValue: 'Settings' }),
      icon: Settings,
      collapsible: true,
      items: [
        { 
          name: translate('layout.navigation.users', { defaultValue: 'Gebruikers' }), 
          href: '/admin/users', 
          icon: UserCog, 
          resource: 'gebruikers', 
          action: 'read' 
        },
        { 
          name: 'Team / Medewerkers', 
          href: '/admin/team', 
          icon: Users, 
          resource: 'gebruikers', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.auditLog', { defaultValue: 'Audit Log' }), 
          href: '/admin/audit', 
          icon: FileSearch, 
          resource: 'admin', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.wipFormulas', { defaultValue: 'WIP Formulas' }), 
          href: '/settings/wip-formulas', 
          icon: Calculator, 
          resource: 'instellingen', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.companyProfile', { defaultValue: 'Company Profile' }), 
          href: '/settings/company', 
          icon: Building2, 
          resource: 'instellingen', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.machineManagement', { defaultValue: 'Machinebeheer' }), 
          href: '/settings/machines', 
          icon: Hammer, 
          resource: 'instellingen', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.safetyVault', { defaultValue: 'Veiligheid (Safety Vault)' }), 
          href: '/settings/safety-vault', 
          icon: Shield, 
          resource: 'instellingen', 
          action: 'read' 
        },
        { 
          name: translate('layout.navigation.bedrijfsDna', { defaultValue: 'Bedrijfs-DNA' }), 
          href: '/settings/dna', 
          icon: Dna, 
          resource: 'instellingen', 
          action: 'read' 
        },
        {
          name: translate('layout.navigation.betalingsvoorwaarden', { defaultValue: 'Betalingsvoorwaarden' }),
          href: '/settings/betalingsvoorwaarden',
          icon: CreditCard,
          resource: 'instellingen',
          action: 'read'
        },
        {
          name: translate('layout.navigation.btwRegimes', { defaultValue: 'Btw-regimes per land' }),
          href: '/settings/btw',
          icon: Receipt,
          resource: 'instellingen',
          action: 'read'
        },
        {
          name: translate('layout.navigation.sketchupKoppeling', { defaultValue: 'SketchUp-koppeling' }),
          href: '/settings/sketchup',
          icon: KeyRound,
          resource: 'instellingen',
          action: 'read',
          requiresModule: 'MODULE_SKETCHUP'
        },
        {
          name: 'AI Assistent',
          href: '/settings/ai',
          icon: Brain,
          resource: 'instellingen',
          action: 'read'
        },
        { 
          name: 'SuperAdmin Dashboard', 
          href: '/admin', 
          icon: Shield, 
          resource: 'superadmin', 
          action: 'read',
          requiresSuperAdmin: true
        },
        {
          name: translate('layout.navigation.saasOverview', { defaultValue: 'SaaS Overview' }),
          href: '/admin/saas',
          icon: BarChart,
          resource: 'admin',
          action: 'admin',
          requiresSuperAdmin: true
        },
        {
          name: translate('layout.navigation.supportTickets', { defaultValue: 'Support tickets' }),
          href: '/admin/support',
          icon: LifeBuoy,
          resource: 'superadmin',
          action: 'read',
          requiresSuperAdmin: true
        },
      ]
    }
    ]
}

/**
 * Navigation hook — memoized zodat de sidebar herrendert bij taalwissel. Leest uit buildNavGroups (SSOT).
 */
export const useNavigation = (t) => {
  const translate = t || ((key, options) => options?.defaultValue || key)
  return useMemo(() => (t ? buildNavGroups(translate) : []), [t])
}

/**
 * Additional navigation items that don't fit into main groups
 */
export const getAdditionalNavigation = (t) => {
  // Ensure t is a function, fallback to identity function if undefined
  const translate = t || ((key, options) => options?.defaultValue || key)
  
  return [
    {
      name: translate('layout.navigation.documenten', { defaultValue: 'Documenten' }),
      href: '/documenten',
      icon: FileText,
      resource: 'documenten',
      action: 'read'
    },
  ]
}

// ─── Vensterregistratie (SSOT voor sidebar én snelknoppen) ────────────────────────────────────────
// NIET kiesbaar als snelknop ("geen bestemming in dezelfde zin"): de startpagina. De mockup-uitsluitingen
// 'werkwachtrij'/'agenten' bestaan niet als los Orion-nav-item, dus niet van toepassing.
export const QUICK_NIET_ELIGIBLE = new Set(['/dashboard'])

// Default snelknop-set — BESTAANDE Orion-vensters (href = stabiele id). Zes stuks. 'agenda' bestond niet als
// eigen Orion-venster → Dirk koos het Montage Planbord (/planning/montage) als zesde.
export const DEFAULT_QUICK_IDS = ['/kasten', '/materialen', '/planning', '/offertes', '/voorraad', '/planning/montage']

// Vlakke registratie van alle navigeerbare vensters — DERIVED uit buildNavGroups + getAdditionalNavigation
// (géén tweede lijst). id = href: stabiel en uniek. Zowel de sidebar-snelknoppen als de tandwiel-kiezer
// putten hieruit; een snelknop-id wordt hiertegen gevalideerd → kan nooit naar een niet-bestaand venster wijzen.
export const getWindowRegistry = (t) => {
  const translate = t || ((key, options) => options?.defaultValue || key)
  const naarVenster = (it, groepId, groepLabel) => ({
    id: it.href,
    label: it.name,
    icon: it.icon,
    route: it.href,
    groepId,
    groepLabel,
    quickEligible: !QUICK_NIET_ELIGIBLE.has(it.href),
  })
  const uitGroepen = buildNavGroups(translate).flatMap((groep) =>
    (groep.items || []).map((it) => naarVenster(it, groep.id, groep.label))
  )
  const overigLabel = translate('layout.navigation.overig', { defaultValue: 'Overig' })
  const uitOverig = getAdditionalNavigation(translate).map((it) => naarVenster(it, 'overig', overigLabel))
  return [...uitGroepen, ...uitOverig]
}

// Resolve een lijst snelknop-id's naar bestaande, kiesbare vensters (dedup, volgorde behouden). Onbekende
// of niet-kiesbare id's vallen weg — de balk toont dus nooit een dode of ongeldige knop.
export const resolveQuickWindows = (ids, t) => {
  const registry = getWindowRegistry(t)
  const perId = new Map(registry.map((w) => [w.id, w]))
  const gezien = new Set()
  return (ids || [])
    .map((id) => perId.get(id))
    .filter((w) => w && w.quickEligible && !gezien.has(w.id) && gezien.add(w.id))
}
